import ImageryDate from 'components/DisplayEnergy/ImageryDate';
import { ImageryIcon, SolarDataIcon } from 'assets/svg';
import { RootState } from 'store';
import { forwardRef, useMemo, useState } from 'react';
import { getRoofAttrsData } from 'components/DisplayRoofData/utils';
import { useSelector } from 'react-redux';
import DesignModesSelector from 'components/ModeSelector';
import FacetListButton from 'components/ManualToolModal/facetListBtn';
import TotalGutterLengthLinearFt from 'components/FacetInputs/TotalGutterLengthLinearft';
import TotalAreaSqFt from 'components/FacetInputs/TotalAreaSqFt';
import ShowFacetLabelsToggle from 'components/DisplayRoofData/showFacetLabelsToggle';
import SaveRoofDesignBtn from 'components/DisplayRoofData/SaveRoofDesignBtn';
import {
	DivSidePanelWrap, DivSolarImagery, MobileViewWrap,
	PanelDetailDefaultView, PanelDetailDiv, RightSidePanel,
	SideModalArrowbutton, EditFacetModalBtnWrap
} from './style';
import DisplayModal from 'components/DisplayModal/DisplayModal';
import { InteractionBlocker } from 'components/InteractionBlocker/interactionBlockerLayer';
import { DESIGN_MODES } from '../../../../constants';
import CustomMapSelect from 'components/CustomMapSelect';
import { selectFilteredGutterLines } from 'store/slices/RoofDataSlice/roofSelector';

const RoofMobileView = forwardRef<HTMLDivElement>((prop, ref) => {
	const { uuid, roofSegsFromWhichUserHasSelectedPanels, panel, org, mode, imageryCapturedDate,
		lidarCapturedDate, roofDataChanging, autoSwitchStatus, billingLogs, modeAndItsCorrespondingImagery, allRoofSegs, roofDesignMode,
	} = useSelector((state: RootState) => ({
		...state.roofData.data,
		roofDataChanging: state.roofData.configuringNewPanel || state.roofData.loading || state.roofData.switchingImageryProvider,
	}));
	const { energyDerateRate, stateCode, toolType, showLidarDate } = useSelector((state: RootState) => state.QueryParams.data);
	const { roofIndexes, editModeEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const someFacetIsStillInEditMode = roofSegsFromWhichUserHasSelectedPanels.filter((ind) => roofIndexes.includes(ind));
	const disableOnEdit = editModeEnabled || !!someFacetIsStillInEditMode.length;

	const filteredGutterLines = useSelector(selectFilteredGutterLines);
	const roofDesignData = useMemo(
		() => getRoofAttrsData(allRoofSegs, filteredGutterLines), [filteredGutterLines, allRoofSegs]);

	const [roofModalOpen, setRoofModalOpen] = useState(false);
	const { fullScreenEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);

	return (
		<RightSidePanel
			ref={ref}
			className={`${fullScreenEnabled ? 'fullScreenView' : 'defaultScreenView'} ${org.setting.isB2C ? 'b2cView' : ''}`}
		>
			{!roofModalOpen &&
			<DivSidePanelWrap>
				<PanelDetailDefaultView className="side-roof-defaultview">
					<TotalGutterLengthLinearFt
						totalGutterlengthLinearFt={roofDesignData.totalGutterlengthLinearFt}
					/>
					{!org.setting.isB2C && (
						<SideModalArrowbutton onClick={() => setRoofModalOpen(true)}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="34"
								height="34"
								viewBox="0 0 52 52"
								fill="none"
							>
								<circle cx="26" cy="26" r="25" fill="var(--defaultBgColor)" stroke="#CCD0D7" />
								<path
									d="M19 26.5L26.5 19L34 26.5"
									stroke="var(--defaultIconColor)"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M19 33.5L26.5 26L34 33.5"
									stroke="var(--defaultIconColor)"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</SideModalArrowbutton>
					)}
					<TotalAreaSqFt totalAreaSqFt={roofDesignData.totalAreaSqFt} />
				</PanelDetailDefaultView>
			</DivSidePanelWrap>
			}

			<DisplayModal
				open={roofModalOpen}
				handleClose={() => setRoofModalOpen(false)}
				className="modal-custom-class sidepanel-modal"
			>
				<MobileViewWrap className="sm-screen roof-sm-screen">
					<SideModalArrowbutton
						onClick={() => setRoofModalOpen(false)}
						className="solarbtn-sidepanel sidepanel-model-close"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="34"
							height="34"
							viewBox="0 0 52 52"
							fill="none"
						>
							<circle cx="26" cy="26" r="25" fill="var(--defaultBgColor)" stroke="#CCD0D7" />
							<path
								d="M19 25.5L26.5 33L34 25.5"
								stroke="var(--defaultIconColor)"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M19 18.5L26.5 26L34 18.5"
								stroke="var(--defaultIconColor)"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</SideModalArrowbutton>
					<InteractionBlocker>

						<PanelDetailDiv className="design-mode-panel design-mode-panel-mobile">
							<DesignModesSelector
								designMode={mode}
								roofMode={roofDesignMode}
								designUUID={uuid}
								panelKey={panel.key}
								energyDerateRate={energyDerateRate}
								stateCode={stateCode}
								billingLogs={billingLogs}
								autoSwitchStatus={autoSwitchStatus}
								modeAndItsCorrespondingImagery={modeAndItsCorrespondingImagery}
								designType="ROOF"
								reqToolType={toolType}
							/>
							<EditFacetModalBtnWrap>
								<FacetListButton onclick={() => setRoofModalOpen(false)} />
							</EditFacetModalBtnWrap>
							<DivSolarImagery className='solar-imagery-wrap'>
								<ImageryDate label="Imagery" date={imageryCapturedDate} 
									icon={<ImageryIcon width="38" height="38" />} />
								{showLidarDate && (
									<ImageryDate label="LiDAR" date={lidarCapturedDate} 
										icon={<SolarDataIcon width="38" height="38" />} />
								)}
							</DivSolarImagery>
							<PanelDetailDiv className="total-gutter-area-wrap-mob">
								<TotalGutterLengthLinearFt
									totalGutterlengthLinearFt={roofDesignData.totalGutterlengthLinearFt}
								/>
								<TotalAreaSqFt totalAreaSqFt={roofDesignData.totalAreaSqFt} />
							</PanelDetailDiv>

							<PanelDetailDiv className='custom-map-roof'>
								{ mode === DESIGN_MODES.NEARMAP_NREL_MANUAL && <CustomMapSelect/>}
							</PanelDetailDiv>
							<ShowFacetLabelsToggle />
						</PanelDetailDiv>

						<MobileViewWrap>
							<SaveRoofDesignBtn
								isB2C={false}
								disabled={disableOnEdit || roofDataChanging}
							/>
						</MobileViewWrap>
					</InteractionBlocker>
				</MobileViewWrap>
			</DisplayModal>
		</RightSidePanel>
	);

});
RoofMobileView.displayName = 'RoofMobileView';
export default RoofMobileView;